import React, { useState } from 'react';
import { Phone, Mail, Shield, Clock, CheckCircle, AlertCircle } from 'lucide-react';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    role: '',
    phone: '',
    timeReclaimed: '',
    message: ''
  });
  
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [errors, setErrors] = useState({});

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    
    // Clear error when user starts typing
    if (errors[e.target.name]) {
      setErrors({
        ...errors,
        [e.target.name]: ''
      });
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) newErrors.name = 'Name is required';
    if (!formData.email.trim()) newErrors.email = 'Email is required';
    if (!formData.company.trim()) newErrors.company = 'Company is required';
    if (!formData.role.trim()) newErrors.role = 'Role is required';
    
    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (formData.email && !emailRegex.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (validateForm()) {
      // Here you would typically send the form data to your backend
      console.log('Form submitted:', formData);
      setIsSubmitted(true);
    }
  };

  if (isSubmitted) {
    return (
      <section id="contact" className="section-padding bg-gray-light">
        <div className="container-center">
          <div className="max-w-2xl mx-auto text-center animate-fade-in">
            <div className="bg-white rounded-3xl p-12 card-shadow">
              <div className="space-y-6">
                <div className="inline-flex p-4 bg-green-100 rounded-full">
                  <CheckCircle className="h-12 w-12 text-green-600" />
                </div>
                <h3 className="font-display text-xl sm:text-2xl md:text-3xl font-bold text-olive-brown">
                  Message Received
                </h3>
                <p className="text-olive-brown/70 text-base sm:text-lg">
                  Your confidential briefing request has been received.
                  A member of our operations team will contact you within 24 hours.
                </p>
                <div className="bg-burnt-orange/10 rounded-lg p-4">
                  <p className="text-burnt-orange font-semibold text-xs sm:text-sm">
                    CONFIDENTIAL: This communication will be treated with complete discretion.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="contact" className="section-padding bg-gray-light">
      <div className="container-center">
        <div className="grid lg:grid-cols-2 gap-16 items-start">
          {/* Contact Information */}
          <div className="space-y-8 animate-slide-in-left">
            <div className="space-y-6">
              <div className="inline-flex items-center space-x-2 bg-burnt-orange/10 px-4 py-2 rounded-full">
                <Shield className="h-4 w-4 text-burnt-orange" />
                <span className="text-sm font-semibold text-burnt-orange">Initiate Contact</span>
              </div>
              
              <h2 className="font-display text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-olive-brown">
                Claim Your <span className="text-gradient">Leverage</span>
              </h2>

              <p className="text-base sm:text-lg md:text-xl text-olive-brown/70 leading-relaxed">
                Ready to eliminate operational friction and reclaim your executive power?
                Begin with a confidential discussion of fit.
              </p>
            </div>

            {/* Contact Details */}
            <div className="space-y-6">
              <div className="bg-white rounded-2xl p-6 card-shadow">
                <div className="flex items-center space-x-4">
                  <div className="p-3 bg-burnt-orange/10 rounded-full">
                    <Phone className="h-6 w-6 text-burnt-orange" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-olive-brown">Direct Line</h4>
                    <p className="text-olive-brown/70">+1 (555) 123-4567</p>
                    <p className="text-sm text-burnt-orange">Executive Priority Line</p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-2xl p-6 card-shadow">
                <div className="flex items-center space-x-4">
                  <div className="p-3 bg-burnt-orange/10 rounded-full">
                    <Mail className="h-6 w-6 text-burnt-orange" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-olive-brown">Secure Contact</h4>
                    <p className="text-olive-brown/70">operations@fractionalva.com</p>
                    <p className="text-sm text-burnt-orange">Encrypted Communication</p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-2xl p-6 card-shadow">
                <div className="flex items-center space-x-4">
                  <div className="p-3 bg-burnt-orange/10 rounded-full">
                    <Clock className="h-6 w-6 text-burnt-orange" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-olive-brown">Response Time</h4>
                    <p className="text-olive-brown/70">Within 24 hours</p>
                    <p className="text-sm text-burnt-orange">Priority Treatment</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Trust Indicators */}
            <div className="bg-gradient-to-r from-burnt-orange/10 to-olive-brown/10 rounded-2xl p-6">
              <h4 className="font-semibold text-olive-brown mb-4">Confidentiality Guaranteed</h4>
              <ul className="space-y-2 text-olive-brown/70 text-sm">
                <li className="flex items-center space-x-2">
                  <div className="w-2 h-2 bg-burnt-orange rounded-full"></div>
                  <span>All communications remain strictly confidential</span>
                </li>
                <li className="flex items-center space-x-2">
                  <div className="w-2 h-2 bg-burnt-orange rounded-full"></div>
                  <span>No sales pressure or unsolicited follow-up</span>
                </li>
                <li className="flex items-center space-x-2">
                  <div className="w-2 h-2 bg-burnt-orange rounded-full"></div>
                  <span>Elite leaders only - verified discretion</span>
                </li>
              </ul>
            </div>
          </div>

          {/* Contact Form */}
          <div className="animate-slide-in-right">
            <div className="bg-white rounded-3xl p-8 card-shadow">
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="text-center space-y-4 mb-8">
                  <h3 className="font-display text-lg sm:text-xl md:text-2xl font-bold text-olive-brown">
                    Confidential Briefing Request
                  </h3>
                  <p className="text-sm sm:text-base text-olive-brown/70">
                    Complete the secure form below to initiate contact
                  </p>
                </div>

                {/* Name and Email Row */}
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="name" className="block text-sm font-semibold text-olive-brown mb-2">
                      Full Name *
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      className={`w-full px-4 py-3 rounded-lg border-2 transition-colors duration-300 focus:outline-none ${
                        errors.name ? 'border-red-300 focus:border-red-500' : 'border-olive-brown/20 focus:border-burnt-orange'
                      }`}
                      placeholder="Your name"
                    />
                    {errors.name && (
                      <p className="text-red-500 text-sm mt-1 flex items-center">
                        <AlertCircle className="h-4 w-4 mr-1" />
                        {errors.name}
                      </p>
                    )}
                  </div>
                  
                  <div>
                    <label htmlFor="email" className="block text-sm font-semibold text-olive-brown mb-2">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      className={`w-full px-4 py-3 rounded-lg border-2 transition-colors duration-300 focus:outline-none ${
                        errors.email ? 'border-red-300 focus:border-red-500' : 'border-olive-brown/20 focus:border-burnt-orange'
                      }`}
                      placeholder="your.email@company.com"
                    />
                    {errors.email && (
                      <p className="text-red-500 text-sm mt-1 flex items-center">
                        <AlertCircle className="h-4 w-4 mr-1" />
                        {errors.email}
                      </p>
                    )}
                  </div>
                </div>

                {/* Company and Role Row */}
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="company" className="block text-sm font-semibold text-olive-brown mb-2">
                      Company *
                    </label>
                    <input
                      type="text"
                      id="company"
                      name="company"
                      value={formData.company}
                      onChange={handleChange}
                      className={`w-full px-4 py-3 rounded-lg border-2 transition-colors duration-300 focus:outline-none ${
                        errors.company ? 'border-red-300 focus:border-red-500' : 'border-olive-brown/20 focus:border-burnt-orange'
                      }`}
                      placeholder="Company name"
                    />
                    {errors.company && (
                      <p className="text-red-500 text-sm mt-1 flex items-center">
                        <AlertCircle className="h-4 w-4 mr-1" />
                        {errors.company}
                      </p>
                    )}
                  </div>
                  
                  <div>
                    <label htmlFor="role" className="block text-sm font-semibold text-olive-brown mb-2">
                      Executive Role *
                    </label>
                    <select
                      id="role"
                      name="role"
                      value={formData.role}
                      onChange={handleChange}
                      className={`w-full px-4 py-3 rounded-lg border-2 transition-colors bg-transparent duration-300 focus:outline-none ${
                        errors.role ? 'border-red-300 focus:border-red-500' : 'border-olive-brown/20 focus:border-burnt-orange'
                      }`}
                    >
                      <option value="">Select your role</option>
                      <option value="CEO">CEO</option>
                      <option value="President">President</option>
                      <option value="COO">COO</option>
                      <option value="CFO">CFO</option>
                      <option value="Founder">Founder</option>
                      <option value="Managing Partner">Managing Partner</option>
                      <option value="Other C-Level">Other C-Level</option>
                    </select>
                    {errors.role && (
                      <p className="text-red-500 text-sm mt-1 flex items-center">
                        <AlertCircle className="h-4 w-4 mr-1" />
                        {errors.role}
                      </p>
                    )}
                  </div>
                </div>

                {/* Phone and Time Reclaimed */}
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="phone" className="block text-sm font-semibold text-olive-brown mb-2">
                      Phone Number
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      className="w-full px-4 py-3 rounded-lg border-2 border-olive-brown/20 focus:border-burnt-orange transition-colors duration-300 focus:outline-none"
                      placeholder="(555) 123-4567"
                    />
                  </div>
                  
                  <div>
                    <label htmlFor="timeReclaimed" className="block text-sm font-semibold text-olive-brown mb-2">
                      Hours to Reclaim Weekly
                    </label>
                    <select
                      id="timeReclaimed"
                      name="timeReclaimed"
                      value={formData.timeReclaimed}
                      onChange={handleChange}
                      className="w-full px-4 py-3 rounded-lg border-2 border-olive-brown/20 focus:border-burnt-orange bg-transparent transition-colors duration-300 focus:outline-none"
                    >
                      <option value="">Select hours</option>
                      <option value="5-10">5-10 hours</option>
                      <option value="10-15">10-15 hours</option>
                      <option value="15-20">15-20 hours</option>
                      <option value="20+">20+ hours</option>
                    </select>
                  </div>
                </div>

                {/* Message */}
                <div>
                  <label htmlFor="message" className="block text-sm font-semibold text-olive-brown mb-2">
                    Operational Priorities
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    rows="4"
                    className="w-full px-4 py-3 rounded-lg border-2 border-olive-brown/20 focus:border-burnt-orange transition-colors duration-300 focus:outline-none resize-none"
                    placeholder="Describe your key operational challenges and priorities..."
                  ></textarea>
                </div>

                {/* Submit Button */}
                <div className="pt-4">
                  <button
                    type="submit"
                    className="w-full btn-primary"
                    aria-label="Submit confidential briefing request"
                  >
                    Submit Confidential Request
                  </button>
                  <p className="text-xs text-olive-brown/60 text-center mt-3">
                    * Required fields. All information treated with complete confidentiality.
                  </p>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;